import React, { useEffect, useMemo, useState, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import FormInput, { FormSelect, FormCheckbox } from '../../setup/components/FormInput';
import { useEFiling } from '../contexts/EFilingContext';
import {
  getCaseCategories,
  getCaseTypes,
  getCaseSubTypes,
  hasCaseSubTypes,
  getCaseCategoryFieldVisibility,
  getCourtJurisdictions,
} from '@lds-efsp/shared-contracts';
import type { CourtJurisdictionRef, CaseCategoryFieldVisibility } from '@lds-efsp/shared-contracts';
import { FormData } from '../../../shared/data/formData';
import AddressFields from '../../../shared/components/AddressFields';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import AutofillChip from './AutofillChip';

const API_BASE = API_BASE_URL;

interface RefOption {
  code: string;
  name: string;
  efmCode?: string;
}

interface CaseInfoTabProps {
  brand: BrandConfig;
  /**
   * Dot-path keys (e.g. "caseInfo.caseTitle") that were populated from OCR.
   * When a field's key is present, an AutofillChip renders next to it.
   * Defaults to an empty set — no chips render (the inert state).
   */
  ocrPopulatedFields?: Set<string>;
  /**
   * Suppress the inline Court Jurisdiction select (Walter R64): the
   * consolidated Confirm page renders it in the Court section beneath County
   * (CourtJurisdictionField via OrderInfoTab's slot) instead. Only the render
   * is suppressed — this component's jurisdiction fetches and auto-selects
   * (single option, zip→courthouse) still run and still write the same
   * caseInfo.courtJurisdiction the relocated select renders.
   */
  hideCourtJurisdiction?: boolean;
}

// Policy-derived case field data
interface CaseFieldsResponse {
  showJurisdictionalAmount: boolean;
  showCourtJurisdiction: boolean;
  showIncidentZip: boolean;
  showAdditionalCaseInfo: boolean;
  jurisdictionalAmountOptions: RefOption[];
  partyRoles: RefOption[];
  motionOscOptions: RefOption[];
  premisesAddressCaseTypes: string[];
  source: string;
}

const EMPTY_OCR_SET: Set<string> = new Set();
const NEEDED_HINT = '⚠ Needed before submitting';

interface HelpTooltipProps {
  text: string;
  fieldName: string;
  brand: BrandConfig;
}

const HelpTooltip: React.FC<HelpTooltipProps> = ({ text, fieldName, brand }) => (
  <span
    role="button"
    tabIndex={0}
    aria-label={`Help: ${fieldName}`}
    title={text}
    data-testid={`help-${fieldName}`}
    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-xs cursor-help focus:outline-none focus:ring-2"
    style={{
      backgroundColor: brand.colors.border,
      color: brand.colors.textLight,
    }}
  >
    ?
  </span>
);

const CaseInfoTab: React.FC<CaseInfoTabProps> = ({
  brand,
  ocrPopulatedFields = EMPTY_OCR_SET,
  hideCourtJurisdiction = false,
}) => {
  const { wizardState, updateCaseInfo, prefetchedCategories, offendingFieldAria } = useEFiling();
  const { caseInfo, orderInfo } = wizardState;
  const { fetchWithAuth } = useApi();
  const [zipError, setZipError] = useState<string>('');
  const [zipWarning, setZipWarning] = useState<string>('');

  const isSubsequent = orderInfo.filingType === 'subsequent-filing';

  // Fetch categories from API (falls back to seed data on error)
  const [categories, setCategories] = useState<RefOption[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- async fetch effects: synchronous reset/loading before fetch, setState in callbacks */
  useEffect(() => {
    if (!orderInfo.county) {
      setCategories([]);
      return;
    }
    // Seed from the context prefetch (warmed when the county was chosen) so the
    // list renders immediately; still revalidate in the background. Only show
    // the spinner when we have nothing to display yet.
    const prefetched = prefetchedCategories?.[orderInfo.county];
    if (prefetched?.length) {
      setCategories(prefetched);
    } else {
      setLoadingCategories(true);
    }
    fetchWithAuth(`${API_BASE}/api/efiling/reference/categories?county=${encodeURIComponent(orderInfo.county)}`)
      .then(res => res.json())
      .then(json => setCategories(json.data || []))
      .catch(() => {
        // Fallback to seed data
        setCategories(getCaseCategories(orderInfo.county));
      })
      .finally(() => setLoadingCategories(false));
  }, [orderInfo.county, fetchWithAuth, prefetchedCategories]);

  // Auto-populate caseCategoryName when code is set (e.g., from GetCase) but name is missing
  useEffect(() => {
    if (caseInfo.caseCategoryCode && !caseInfo.caseCategoryName && categories.length > 0) {
      const match = categories.find(c => (c.efmCode || c.code) === caseInfo.caseCategoryCode);
      if (match) {
        updateCaseInfo({ caseCategoryName: match.name });
      }
    }
  }, [caseInfo.caseCategoryCode, caseInfo.caseCategoryName, categories, updateCaseInfo]);

  // OCR deferred match: when category name is set (from extraction) but code is empty,
  // match against fetched categories by name (case-insensitive). The deferred-match
  // useEffects key off the short-name OCR markers populated by the wizard context
  // (e.g., 'caseCategoryName'); the chip keys off dot-paths (e.g.,
  // 'caseInfo.caseCategoryName'). Both conventions can coexist in the same set.
  useEffect(() => {
    if (!caseInfo.caseCategoryName || caseInfo.caseCategoryCode || categories.length === 0) return;
    if (!ocrPopulatedFields.has('caseCategoryName')) return;
    const nameLower = caseInfo.caseCategoryName.toLowerCase();
    const match = categories.find(c => c.name.toLowerCase() === nameLower);
    if (match) {
      updateCaseInfo({ caseCategoryCode: match.efmCode || match.code });
    }
  }, [caseInfo.caseCategoryName, caseInfo.caseCategoryCode, categories, ocrPopulatedFields, updateCaseInfo]);

  const categoryOptions = [
    { value: '', label: loadingCategories ? 'Loading...' : 'Select case category...' },
    ...categories.map(c => ({ value: c.efmCode || c.code, label: c.name })),
  ];

  // Fetch case types from API when category changes
  const [caseTypes, setCaseTypes] = useState<RefOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  useEffect(() => {
    if (!caseInfo.caseCategoryCode) {
      setCaseTypes([]);
      return;
    }
    setLoadingTypes(true);
    fetchWithAuth(`${API_BASE}/api/efiling/reference/types/${encodeURIComponent(caseInfo.caseCategoryCode)}?county=${encodeURIComponent(orderInfo.county)}`)
      .then(res => res.json())
      .then(json => setCaseTypes(json.data || []))
      .catch(() => {
        // Fallback to seed data
        setCaseTypes(getCaseTypes(caseInfo.caseCategoryCode));
      })
      .finally(() => setLoadingTypes(false));
  }, [caseInfo.caseCategoryCode, orderInfo.county, fetchWithAuth]);

  // OCR deferred match: when case type name is set (from extraction) but code is empty,
  // match against fetched types by name (case-insensitive)
  useEffect(() => {
    if (!caseInfo.caseType?.name || caseInfo.caseType?.code || caseTypes.length === 0) return;
    if (!ocrPopulatedFields.has('caseTypeName')) return;
    const nameLower = caseInfo.caseType.name.toLowerCase();
    const match = caseTypes.find(ct => ct.name.toLowerCase() === nameLower);
    if (match) {
      updateCaseInfo({ caseType: { code: match.code, name: match.name } });
    }
  }, [caseInfo.caseType?.name, caseInfo.caseType?.code, caseTypes, ocrPopulatedFields, updateCaseInfo]);

  const caseTypeOptions = [
    { value: '', label: loadingTypes ? 'Loading...' : 'Select case type...' },
    ...caseTypes.map(ct => ({ value: ct.code, label: ct.name })),
  ];

  // Get sub-types for selected case type
  const caseTypeCode = caseInfo.caseType?.code || '';
  const showSubTypes = hasCaseSubTypes(caseTypeCode);
  const caseSubTypes = useMemo(
    () => showSubTypes ? getCaseSubTypes(caseTypeCode) : [],
    [caseTypeCode, showSubTypes],
  );

  const caseSubTypeOptions = [
    { value: '', label: 'Select case sub-type...' },
    ...caseSubTypes.map(st => ({ value: st.code, label: st.name })),
  ];

  // Policy-driven field visibility and options — single API call per category
  const [caseFields, setCaseFields] = useState<CaseFieldsResponse | null>(null);
  const [loadingCaseFields, setLoadingCaseFields] = useState(false);

  useEffect(() => {
    if (!caseInfo.caseCategoryCode || !orderInfo.county) {
      setCaseFields(null);
      return;
    }
    setLoadingCaseFields(true);
    const params = new URLSearchParams({
      county: orderInfo.county,
      categoryCode: caseInfo.caseCategoryCode,
    });
    fetchWithAuth(`${API_BASE}/api/efiling/reference/case-fields?${params}`)
      .then(res => res.json())
      .then(data => setCaseFields(data))
      .catch(() => {
        // Fallback to hardcoded seed data
        const vis = getCaseCategoryFieldVisibility(caseInfo.caseCategoryCode);
        setCaseFields({
          ...vis,
          jurisdictionalAmountOptions: [],
          partyRoles: [],
          motionOscOptions: [],
          premisesAddressCaseTypes: [],
          source: 'seed-fallback',
        });
      })
      .finally(() => setLoadingCaseFields(false));
  }, [caseInfo.caseCategoryCode, orderInfo.county, fetchWithAuth]);

  // Derive field visibility from policy response
  const fieldVisibility: CaseCategoryFieldVisibility | null = caseFields ? {
    showJurisdictionalAmount: caseFields.showJurisdictionalAmount,
    showCourtJurisdiction: caseFields.showCourtJurisdiction,
    showIncidentZip: caseFields.showIncidentZip,
    showAdditionalCaseInfo: caseFields.showAdditionalCaseInfo,
  } : null;

  // Court Jurisdiction options — from case-fields response
  const [courtJurisdictions, setCourtJurisdictions] = useState<RefOption[]>([]);
  const [loadingJurisdictions, setLoadingJurisdictions] = useState(false);
  // County the current options list was fetched for. Guards the single-option
  // auto-select against a stale list (WOL-420 / Walter R74): on a county
  // change the value is cleared (WOL-341), but until the refetch lands the
  // list still holds the OLD county's single option — auto-selecting from it
  // re-filled the just-cleared value with the old county's courthouse. Classic
  // flow never hit this (this tab is unmounted while county changes on step
  // 2); the confirm flow mounts both sections together.
  const [jurisdictionsCounty, setJurisdictionsCounty] = useState<string | null>(null);

  useEffect(() => {
    if (!caseFields?.showCourtJurisdiction) {
      setCourtJurisdictions([]);
      setJurisdictionsCounty(null);
      return;
    }
    // Drop the previous county's list synchronously so it can never be
    // auto-selected or displayed while the new county's fetch is in flight.
    setCourtJurisdictions([]);
    setJurisdictionsCounty(null);
    // Fetch court jurisdictions separately (they come from COURT_LOCATIONS, not case-fields)
    setLoadingJurisdictions(true);
    const fetchedForCounty = orderInfo.county;
    const params = new URLSearchParams({
      county: orderInfo.county,
      categoryCode: caseInfo.caseCategoryCode,
    });
    fetchWithAuth(`${API_BASE}/api/efiling/reference/court-jurisdictions?${params}`)
      .then(res => res.json())
      .then(json => {
        setCourtJurisdictions(json.data || []);
        setJurisdictionsCounty(fetchedForCounty);
      })
      .catch(() => {
        setCourtJurisdictions(getCourtJurisdictions(caseInfo.caseCategoryCode));
        setJurisdictionsCounty(fetchedForCounty);
      })
      .finally(() => setLoadingJurisdictions(false));
  }, [caseFields?.showCourtJurisdiction, caseInfo.caseCategoryCode, orderInfo.county, fetchWithAuth]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  // Auto-select when there's exactly one court jurisdiction — but only from a
  // list fetched for the CURRENT county (WOL-420 / R74).
  useEffect(() => {
    if (
      courtJurisdictions.length === 1 &&
      !caseInfo.courtJurisdiction &&
      jurisdictionsCounty === orderInfo.county
    ) {
      const only = courtJurisdictions[0];
      updateCaseInfo({
        courtJurisdiction: { code: only.code, name: only.name } as CourtJurisdictionRef,
      });
    }
  }, [courtJurisdictions, caseInfo.courtJurisdiction, jurisdictionsCounty, orderInfo.county, updateCaseInfo]);

  const courtJurisdictionOptions = useMemo(
    () => [
      { value: '', label: loadingJurisdictions ? 'Loading...' : 'Select court jurisdiction...' },
      ...courtJurisdictions.map(cj => ({
        value: cj.code,
        label: cj.name,
      })),
    ],
    [courtJurisdictions, loadingJurisdictions],
  );

  // JA options from policy (replaces hardcoded list)
  const jurisdictionalAmountOptions = useMemo(
    () => [
      { value: '', label: loadingCaseFields ? 'Loading...' : 'Select...' },
      ...(caseFields?.jurisdictionalAmountOptions || []).map(ja => ({
        value: ja.code,
        label: ja.name,
      })),
    ],
    [caseFields?.jurisdictionalAmountOptions, loadingCaseFields],
  );

  // Show Appellate-specific fields
  const isAppellate = caseInfo.caseCategoryCode === 'AP';

  // Additional info section visibility
  const showAdditionalInfo = fieldVisibility?.showAdditionalCaseInfo ?? (caseInfo.caseTypeMetadata?.showCasePlugin !== false);
  // Premises address: show when selected case type is in the policy's premisesAddressCaseTypes list
  const showPremisesAddress = caseInfo.caseType?.code
    ? (caseFields?.premisesAddressCaseTypes || []).includes(caseInfo.caseType.code)
    : false;

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const efmCode = e.target.value;
    const cat = categories.find(c => (c.efmCode || c.code) === efmCode);
    updateCaseInfo({
      caseCategoryCode: efmCode,
      caseCategoryName: cat?.name || '',
      caseType: null,
      caseSubType: null,
      jurisdictionalAmount: '',
      courtJurisdiction: null,
    });
  };

  const handleCaseTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    const ct = caseTypes.find(t => t.code === code);
    updateCaseInfo({
      caseType: ct ? { code: ct.code, name: ct.name } : null,
      caseSubType: null,
    });
  };

  const handleSubTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    const st = caseSubTypes.find(s => s.code === code);
    updateCaseInfo({
      caseSubType: st ? { code: st.code, name: st.name } : null,
    });
  };

  const handleZipBlur = useCallback(() => {
    const zip = caseInfo.incidentZip;
    if (!zip) {
      setZipError('');
      setZipWarning('');
      return;
    }
    if (!/^\d{5}(-\d{4})?$/.test(zip)) {
      setZipError('Please enter a valid 5-digit zip code.');
      setZipWarning('');
      return;
    }
    if (!orderInfo.county) {
      setZipError('');
      setZipWarning('');
      return;
    }

    // Check zip against court's jurisdiction mapping (auto-selects courthouse)
    fetchWithAuth(`${API_BASE}/api/efiling/reference/zip-lookup?county=${encodeURIComponent(orderInfo.county)}&zip=${encodeURIComponent(zip)}`)
      .then(res => res.json())
      .then(result => {
        if (!result.valid) {
          // Soft warning — don't block, court (JTI) has final say
          setZipError('');
          setZipWarning(result.warning || `Zip code ${zip} may not be in ${orderInfo.county}'s jurisdiction. The court may reject this filing.`);
        } else {
          setZipError('');
          setZipWarning('');
          // Auto-select courthouse from zip match
          if (result.match && !caseInfo.courtJurisdiction) {
            updateCaseInfo({
              courtJurisdiction: {
                code: result.match.locationCode,
                name: result.match.locationName,
              } as CourtJurisdictionRef,
            });
          }
        }
      })
      .catch(() => {
        // If API is unavailable, accept the zip (fail open)
        setZipError('');
        setZipWarning('');
      });
  }, [caseInfo.incidentZip, orderInfo.county, caseInfo.courtJurisdiction, updateCaseInfo, fetchWithAuth]);

  const handleAdditionalInfoChange = (field: string, value: boolean) => {
    const updates = { ...caseInfo.additionalInfo, [field]: value };
    // If complex case is unchecked, also uncheck class action
    if (field === 'complexCase' && !value) {
      updates.classActionIndicator = false;
    }
    updateCaseInfo({ additionalInfo: updates });
  };

  const handlePremisesAddressChange = (field: string, value: string) => {
    updateCaseInfo({
      premisesAddress: { ...caseInfo.premisesAddress, [field]: value },
    });
  };

  // Chip visibility per field (keyed by state dot-path)
  const showCaseTitleChip = ocrPopulatedFields.has('caseInfo.caseTitle');
  const showCategoryChip =
    ocrPopulatedFields.has('caseInfo.caseCategoryCode') ||
    ocrPopulatedFields.has('caseInfo.caseCategoryName');
  const showCaseTypeChip =
    ocrPopulatedFields.has('caseInfo.caseType') ||
    ocrPopulatedFields.has('caseInfo.caseType.name');
  const showIncidentZipChip = ocrPopulatedFields.has('caseInfo.incidentZip');

  // Category/type already set when this step mounted were carried over from a
  // prior step (the Upload step's in-card picker, or a case attach) — label
  // them so the filer knows they're pre-filled, not blank work to redo
  // (Walter R38). The OCR chip covers the extraction case; this covers the
  // manual-pick case. The pill drops as soon as the filer changes the value
  // here. useState initializer = mount-time snapshot the react-compiler lint
  // accepts (a ref read during render is flagged).
  const [carriedCase] = useState({
    category: caseInfo.caseCategoryCode,
    caseType: caseInfo.caseType?.code,
  });
  const categoryCarried =
    !showCategoryChip &&
    !!carriedCase.category &&
    caseInfo.caseCategoryCode === carriedCase.category;
  const caseTypeCarried =
    !showCaseTypeChip &&
    !!carriedCase.caseType &&
    caseInfo.caseType?.code === carriedCase.caseType;
  const carriedPill = (visible: boolean, field: string) =>
    visible ? (
      <span
        data-testid={`carried-${field}`}
        className="text-[11px] font-medium px-2 py-0.5 rounded-full"
        style={{ backgroundColor: `${brand.colors.primary}14`, color: brand.colors.link }}
      >
        carried over
      </span>
    ) : null;

  // Required-and-empty amber treatment for required fields
  const categoryNeedsValue = !isSubsequent && !caseInfo.caseCategoryCode;
  const caseTypeNeedsValue = !isSubsequent && !!caseInfo.caseCategoryCode && !caseInfo.caseType;
  const courtJurisdictionNeedsValue =
    !isSubsequent &&
    !!caseInfo.caseType &&
    !!fieldVisibility?.showCourtJurisdiction &&
    !caseInfo.courtJurisdiction;

  return (
    <div className="max-w-2xl">
      <h2
        className="text-lg font-semibold mb-4"
        style={{ color: brand.colors.text }}
      >
        Case Information
      </h2>

      <FormInput
        brand={brand}
        label="Case Title"
        name="caseTitle"
        value={caseInfo.caseTitle}
        onChange={e => updateCaseInfo({ caseTitle: e.target.value })}
        placeholder='e.g. "John Doe v Williams"'
        required
        disabled={isSubsequent}
        {...offendingFieldAria('caseTitle')}
        labelAdornment={<AutofillChip visible={showCaseTitleChip} />}
      />

      {/* Row 1: Case Category + Case Type — side by side */}
      <div
        data-testid="row-category-type"
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <FormSelect
          brand={brand}
          label="Case Category"
          name="caseCategory"
          value={caseInfo.caseCategoryCode}
          onChange={handleCategoryChange}
          options={categoryOptions}
          required
          disabled={isSubsequent}
          labelAdornment={
            <>
              <HelpTooltip
                fieldName="caseCategory"
                text="The broad legal classification of the case (e.g. Civil, Family, Probate). Required by the court to route the filing."
                brand={brand}
              />
              <AutofillChip visible={showCategoryChip} />
              {carriedPill(categoryCarried, 'caseCategory')}
            </>
          }
          validationStatus={categoryNeedsValue ? 'warning' : undefined}
          validationMessage={categoryNeedsValue ? NEEDED_HINT : undefined}
        />

        <FormSelect
          brand={brand}
          label="Case Type"
          name="caseType"
          value={caseInfo.caseType?.code || ''}
          onChange={handleCaseTypeChange}
          options={caseTypeOptions}
          required
          disabled={!caseInfo.caseCategoryCode}
          helpText={!caseInfo.caseCategoryCode ? 'Select a case category first'
            : isSubsequent && !caseInfo.caseType ? 'Select the case type for this filing'
            : undefined}
          labelAdornment={
            <>
              <AutofillChip visible={showCaseTypeChip} />
              {carriedPill(caseTypeCarried, 'caseType')}
            </>
          }
          validationStatus={caseTypeNeedsValue ? 'warning' : undefined}
          validationMessage={caseTypeNeedsValue ? NEEDED_HINT : undefined}
        />
      </div>

      {/* Case Sub Type — only for certain types (e.g., Appellate) */}
      {showSubTypes && (
        <FormSelect
          brand={brand}
          label="Case Sub Type"
          name="caseSubType"
          value={caseInfo.caseSubType?.code || ''}
          onChange={handleSubTypeChange}
          options={caseSubTypeOptions}
          required
        />
      )}

      {/* Trial Court Case # — Appellate only */}
      {isAppellate && (
        <FormInput
          brand={brand}
          label="Trial Court Case #"
          name="trialCourtCaseNumber"
          value={caseInfo.trialCourtCaseNumber}
          onChange={e => updateCaseInfo({ trialCourtCaseNumber: e.target.value })}
          required
        />
      )}

      {/* Row 2: Jurisdictional Amount + Incident Zip — side by side; each cell is empty
          when its policy gate is closed, leaving the other half-width but visible. */}
      <div
        data-testid="row-amount-zip"
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <div>
          {!isSubsequent && caseInfo.caseType && fieldVisibility?.showJurisdictionalAmount && (
            <FormSelect
              brand={brand}
              label="Jurisdictional Amount"
              name="jurisdictionalAmount"
              value={caseInfo.jurisdictionalAmount}
              onChange={e => updateCaseInfo({
                jurisdictionalAmount: e.target.value as any,
              })}
              options={jurisdictionalAmountOptions}
              labelAdornment={
                <HelpTooltip
                  fieldName="jurisdictionalAmount"
                  text="The court-fee tier driven by the amount in controversy. Determines the filing-fee bracket the court charges."
                  brand={brand}
                />
              }
            />
          )}
        </div>

        <div>
          {!isSubsequent &&
           (fieldVisibility?.showIncidentZip !== false) &&
           (caseInfo.caseTypeMetadata?.isIncidentZipAvailable !== false) && (
            <FormInput
              brand={brand}
              label="Incident Zip"
              name="incidentZip"
              value={caseInfo.incidentZip}
              onChange={e => {
                updateCaseInfo({ incidentZip: e.target.value });
                if (zipError) setZipError('');
                if (zipWarning) setZipWarning('');
              }}
              onBlur={handleZipBlur}
              placeholder="Enter zip code"
              required
              error={zipError}
              {...offendingFieldAria('incidentZip')}
              validationStatus={zipWarning ? 'warning' : undefined}
              validationMessage={zipWarning}
              labelAdornment={<AutofillChip visible={showIncidentZipChip} />}
            />
          )}
        </div>
      </div>

      {/* Court Jurisdiction — Family and other categories, not for subsequent
          filings. Hidden on the consolidated Confirm page, which renders it in
          the Court section beneath County instead (Walter R64). */}
      {!hideCourtJurisdiction && !isSubsequent && caseInfo.caseType && fieldVisibility?.showCourtJurisdiction && (
        <FormSelect
          brand={brand}
          label="Court Jurisdiction"
          name="courtJurisdiction"
          value={caseInfo.courtJurisdiction?.code || ''}
          onChange={e => {
            const code = e.target.value;
            const opt = courtJurisdictions.find(cj => cj.code === code);
            updateCaseInfo({
              courtJurisdiction: opt ? { code: opt.code, name: opt.name } as CourtJurisdictionRef : null,
            });
          }}
          options={courtJurisdictionOptions}
          required
          validationStatus={courtJurisdictionNeedsValue ? 'warning' : undefined}
          validationMessage={courtJurisdictionNeedsValue ? NEEDED_HINT : undefined}
        />
      )}

      {/* Additional case details — gated by policy + case-type metadata, hidden for
          subsequent filings. Wrapped in a native <details> collapsed by default.
          Presentation-only change: gating rules upstream are unchanged. */}
      {!isSubsequent &&
       fieldVisibility?.showAdditionalCaseInfo !== false &&
       showAdditionalInfo && caseInfo.incidentZip && !zipError && (
        <details
          data-testid="additional-case-details"
          className="mt-6 p-4 rounded-lg border"
          style={{ borderColor: brand.colors.border }}
        >
          <summary
            className="text-sm font-semibold cursor-pointer select-none"
            style={{ color: brand.colors.text }}
          >
            Additional case details
          </summary>

          <div className="mt-3">
            <FormCheckbox
              brand={brand}
              label="Conditionally Under Seal"
              name="conditionallyUnderSeal"
              checked={caseInfo.additionalInfo.conditionallyUnderSeal}
              onChange={e => handleAdditionalInfoChange('conditionallyUnderSeal', e.target.checked)}
            />

            <FormCheckbox
              brand={brand}
              label="Confidential Case"
              name="confidentialCase"
              checked={caseInfo.additionalInfo.confidentialCase}
              onChange={e => handleAdditionalInfoChange('confidentialCase', e.target.checked)}
            />

            <FormCheckbox
              brand={brand}
              label="Complex Case"
              name="complexCase"
              checked={caseInfo.additionalInfo.complexCase}
              onChange={e => handleAdditionalInfoChange('complexCase', e.target.checked)}
              helpText={caseInfo.additionalInfo.complexCase
                ? 'Additional $1,000 fee per GC 70616(a)'
                : undefined}
            />

            {/* Complex case warning */}
            {caseInfo.additionalInfo.complexCase && (
              <>
                <div
                  className="ml-6 p-2 rounded text-xs mb-3"
                  style={{
                    backgroundColor: `${brand.colors.warning || '#F59E0B'}10`,
                    color: brand.colors.warning || '#F59E0B',
                    border: `1px solid ${brand.colors.warning || '#F59E0B'}30`,
                  }}
                >
                  Complex case designation adds $1,000 to court fees (GC 70616(a))
                </div>

                <div className="ml-6">
                  <FormCheckbox
                    brand={brand}
                    label="Class Action Indicator"
                    name="classActionIndicator"
                    checked={caseInfo.additionalInfo.classActionIndicator}
                    onChange={e => handleAdditionalInfoChange('classActionIndicator', e.target.checked)}
                  />
                </div>
              </>
            )}

            {/* Demand Amount */}
            <FormInput
              brand={brand}
              label="Demand Amount"
              name="demandAmount"
              type="number"
              value={caseInfo.demandAmount}
              onChange={e => updateCaseInfo({ demandAmount: parseFloat(e.target.value) || 0 })}
              placeholder="$0.00"
            />

            {/* Premises Address */}
            {showPremisesAddress && (
              <div className="mt-4">
                <h4
                  className="text-sm font-medium mb-2"
                  style={{ color: brand.colors.text }}
                >
                  Premises Address
                </h4>
                <AddressFields
                  brand={brand}
                  prefix="premises"
                  value={{
                    street1: caseInfo.premisesAddress.address1 || '',
                    street2: caseInfo.premisesAddress.address2 ?? '',
                    city: caseInfo.premisesAddress.city || '',
                    state: caseInfo.premisesAddress.state || '',
                    zip: caseInfo.premisesAddress.zip || '',
                  }}
                  onChange={addr => {
                    // Route only the field that actually changed through the
                    // existing handler. handlePremisesAddressChange closes over a
                    // stale caseInfo.premisesAddress, so calling it for every
                    // field at once would clobber all but the last write.
                    const prev = {
                      address1: caseInfo.premisesAddress.address1 || '',
                      address2: caseInfo.premisesAddress.address2 ?? '',
                      city: caseInfo.premisesAddress.city || '',
                      state: caseInfo.premisesAddress.state || '',
                      zip: caseInfo.premisesAddress.zip || '',
                    };
                    if (addr.street1 !== prev.address1) handlePremisesAddressChange('address1', addr.street1);
                    if ((addr.street2 ?? '') !== prev.address2) handlePremisesAddressChange('address2', addr.street2 ?? '');
                    if (addr.city !== prev.city) handlePremisesAddressChange('city', addr.city);
                    if (addr.state !== prev.state) handlePremisesAddressChange('state', addr.state);
                    if (addr.zip !== prev.zip) handlePremisesAddressChange('zip', addr.zip);
                  }}
                  showStreet2
                  statesOptions={FormData.states}
                  street1Label="Address 1"
                  street2Label="Address 2"
                />
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
};

export default CaseInfoTab;
